package dev.kuml.portal.web.editor

import kotlinx.serialization.Serializable
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

@Serializable
public data class PresencePayloadV1(
    val v: Int = 1,
    val c: List<Double>? = null,
    val s: List<String> = emptyList(),
)

/** One decoded presence payload -- see [PresencePayload.decode]. Both fields default to "nothing
 * known" so every failure path there can return a single shared value. */
public data class DecodedPresence(
    val cursor: Pair<Double, Double>? = null,
    val selection: List<String> = emptyList(),
)

/** The diagram's current viewBox, in the same units [PresencePayload.decode]'s `c` field is
 * expressed in -- see `EditorScreen.kt`'s `currentViewBoxRect`. */
public data class ViewBoxRect(
    val minX: Double,
    val minY: Double,
    val width: Double,
    val height: Double,
)

/**
 * R-28 (kUML Portal "Cursor-/Selektions-Sharing" wave): wire payload carried inside a
 * [CollabFrame.KIND_PRESENCE] frame's opaque bytes (see that object's class KDoc) --
 * `kuml-portal-server`'s `CollabRoomRegistry.publishPresence` never reads these bytes itself (see
 * its own class KDoc "opaque"), only relays them, so this whole codec is entirely this client's own
 * contract with itself (every other kUML Portal web client, present and future). [encode]/[decode]
 * are pure, DOM-free -- see `PresencePayloadTest`.
 *
 * Every payload this client ever sends is a COMPLETE snapshot, never a delta -- [PresencePayloadV1
 * .c] carries the current cursor position (or is omitted entirely for an idle/leave/dispose
 * payload) and [PresencePayloadV1.s] ALWAYS carries the full current selection, even when only the
 * cursor moved. [decode] therefore always REPLACES a peer's previous cursor/selection wholesale
 * (see `PresenceState.withUpdate`), never merges -- there is no partial-update case to reason about
 * anywhere in this pipeline.
 */
public object PresencePayload {
    /** Soft client-side budget [encode] enforces by trimming [PresencePayloadV1.s] from the end --
     * well under the server's own hard [MAX_RAW_BYTES] cap, which stays the actual authority. */
    public const val MAX_ENCODED_BYTES: Int = 2_048

    /** [decode] keeps at most this many [PresencePayloadV1.s] entries -- the rest are dropped
     * (order-preserving), a defensive cap independent of [MAX_ENCODED_BYTES]/[MAX_RAW_BYTES] for a
     * forged payload that packs many very short ids. */
    public const val MAX_SELECTION_IDS: Int = 32

    /** A single [PresencePayloadV1.s] entry longer than this is DROPPED outright by [decode], never
     * truncated -- a truncated id could collide with a real, shorter id in [DecodedPresence
     * .selection] and highlight the wrong element. [encode] applies the same limit to what it is
     * willing to send in the first place. */
    public const val MAX_SELECTION_ID_CHARS: Int = 128

    /** [decode] refuses anything larger than this outright -- mirrors the server's own hard
     * `CollabRoomRegistry.MAX_PRESENCE_BYTES` cap (8 KiB), so a decode never even attempts to parse
     * a payload the server itself would have rejected via `publishPresence`'s `require(...)` had
     * this connection tried to SEND one this large. */
    public const val MAX_RAW_BYTES: Int = 8_192

    /** The empty, kind-byte-less payload for a dispose/explicit-clear presence frame -- decodes on
     * every OTHER peer as `present = false` (see `CollabFrame.decodePresence`'s KDoc), exactly like
     * this connection dropping out entirely. */
    public val EMPTY: ByteArray = ByteArray(0)

    /** The very first presence frame this connection ever sends, right after the WebSocket opens
     * (`CollabClient.kt`'s `ws.onopen`) -- a valid, non-empty v1 payload with no cursor/selection
     * yet, so THIS connection is immediately `present = true` for every peer, exactly like the
     * pre-R-28 `"$tabId:$joinedAtMillis"` liveness string it replaces. */
    public val INITIAL: ByteArray = "{\"v\":1}".encodeToByteArray()

    private val json = Json { ignoreUnknownKeys = true }

    /**
     * Builds the UTF-8 JSON bytes for one outgoing presence frame (WITHOUT the [CollabFrame
     * .KIND_PRESENCE] kind byte -- `CollabClient.sendPresence` prepends that). [selection] entries
     * longer than [MAX_SELECTION_ID_CHARS] are dropped BEFORE the size budget below is even
     * considered. If the result still exceeds [MAX_ENCODED_BYTES], [selection] is trimmed from the
     * END, one entry at a time, until it fits (or is empty) -- [cursor] is NEVER dropped to make
     * room; a cursor is one pair of doubles and can never itself be the reason a payload is
     * oversized, so a caller can always rely on this returning cursor data whenever [cursor] is
     * non-null.
     */
    public fun encode(
        cursor: Pair<Double, Double>?,
        selection: List<String>,
    ): ByteArray {
        val cField = cursor?.let { listOf(it.first, it.second) }
        var candidate = selection.filter { it.length <= MAX_SELECTION_ID_CHARS }
        while (true) {
            val bytes = json.encodeToString(PresencePayloadV1(c = cField, s = candidate)).encodeToByteArray()
            if (bytes.size <= MAX_ENCODED_BYTES || candidate.isEmpty()) return bytes
            candidate = candidate.dropLast(1)
        }
    }

    /**
     * `DecodedPresence()` (both fields empty) -- NEVER throws -- for: a raw payload over
     * [MAX_RAW_BYTES]; one with no leading `{` (the pre-R-28 `"$tabId:$joinedAtMillis"` liveness
     * string a not-yet-upgraded peer tab could still send, or any other non-JSON garbage); a
     * malformed/undecodable JSON body; or `v != 1` (a hypothetical future incompatible schema this
     * client does not understand). An unrecognised EXTRA field inside otherwise-valid JSON is
     * silently ignored ([Json]'s `ignoreUnknownKeys = true`, exactly like [ModelOpCodec]'s codec) --
     * never treated as a decode failure.
     *
     * A present [DecodedPresence.cursor]'s two coordinates are clamped into `[viewBox ± 20% of
     * width/height]` -- generous enough that a cursor near the diagram's edge during a fast pointer
     * move is never clipped away, while still bounding a forged/corrupted payload from placing a
     * peer's cursor arbitrarily far off-canvas. A non-finite (`NaN`/`Infinity`) coordinate decodes
     * the cursor as absent (`null`) rather than propagating a non-finite value into
     * `PresenceOverlay.kt`'s SVG `transform` attribute.
     *
     * [DecodedPresence.selection] keeps only entries that are also members of [knownIds] (this
     * connection's own current outline) AND at most [MAX_SELECTION_ID_CHARS] characters long -- a
     * peer selecting an id this connection has never heard of (a race with a not-yet-arrived
     * outline refresh, or a forged id) is simply dropped, exactly like [DiagramSelection.restore]'s
     * own "unknown id -> drop it" convention. The length check defends against a forged/hand-built
     * payload that skips this client's own [encode] (which already never produces an oversized id
     * in the first place) -- security-review requirement, never trust a peer's raw bytes to already
     * satisfy the limits this client's own encoder enforces on itself. Capped at [MAX_SELECTION_IDS]
     * entries overall, order preserved.
     */
    public fun decode(
        raw: String,
        viewBox: ViewBoxRect,
        knownIds: Set<String>,
    ): DecodedPresence {
        if (raw.encodeToByteArray().size > MAX_RAW_BYTES) return DecodedPresence()
        if (!raw.trimStart().startsWith("{")) return DecodedPresence()
        val parsed = runCatching { json.decodeFromString<PresencePayloadV1>(raw) }.getOrNull() ?: return DecodedPresence()
        if (parsed.v != 1) return DecodedPresence()
        val cursor = decodeCursor(parsed.c, viewBox)
        val selection =
            parsed.s
                .filter { it.length <= MAX_SELECTION_ID_CHARS && it in knownIds }
                .take(MAX_SELECTION_IDS)
        return DecodedPresence(cursor = cursor, selection = selection)
    }

    private fun decodeCursor(
        c: List<Double>?,
        viewBox: ViewBoxRect,
    ): Pair<Double, Double>? {
        if (c == null || c.size != 2) return null
        val x = c[0]
        val y = c[1]
        if (!x.isFinite() || !y.isFinite()) return null
        val minX = viewBox.minX - viewBox.width * 0.2
        val maxX = viewBox.minX + viewBox.width * 1.2
        val minY = viewBox.minY - viewBox.height * 0.2
        val maxY = viewBox.minY + viewBox.height * 1.2
        return x.coerceIn(minX, maxX) to y.coerceIn(minY, maxY)
    }
}
